import React, { useState } from "react";
import { CheckCircle2, Circle, ChevronRight, Loader } from "lucide-react";

export const DEPOSIT_FOLLOW_UP_CHECKPOINTS = [
  {
    value: "WaitingForDeposit",
    label: "Waiting For Deposit",
    labelAr: "بانتظار الديبوزت",
  },
  {
    value: "WantToCompleteOrder",
    label: "Want To Complete Order",
    labelAr: "حابب تكمل الاوردر؟",
  },
  {
    value: "DepositFirstCall",
    label: "First Call",
    labelAr: "المكالمة الأولى",
  },
];

export const DEPOSIT_FOLLOW_UP_VALUES = {
  WAITING_FOR_DEPOSIT: "WaitingForDeposit",
  WANT_TO_COMPLETE: "WantToCompleteOrder",
  FIRST_CALL: "DepositFirstCall",
};

const NEW_ORDER_CHECKPOINTS = new Set([
  "SendConfirmationWhatsApp",
  "WaitingForTap",
  "WaitingForSecondaryConfirmation",
  "FirstCall",
  "SecondCall",
  "SendReminderWhatsApp",
]);

export const normalizeDepositFollowUpCheckpoint = (checkpoint) => {
  const match = DEPOSIT_FOLLOW_UP_CHECKPOINTS.find(
    (c) => c.value.toLowerCase() === (checkpoint || "").toLowerCase()
  );
  if (match) return match.value;
  if (!checkpoint || NEW_ORDER_CHECKPOINTS.has(checkpoint)) {
    return DEPOSIT_FOLLOW_UP_VALUES.WAITING_FOR_DEPOSIT;
  }
  return DEPOSIT_FOLLOW_UP_VALUES.WAITING_FOR_DEPOSIT;
};

export const getDepositFollowUpCheckpointIndex = (checkpoint) =>
  DEPOSIT_FOLLOW_UP_CHECKPOINTS.findIndex(
    (c) => c.value === normalizeDepositFollowUpCheckpoint(checkpoint)
  );

export const getNextDepositFollowUpCheckpoint = (checkpoint) => {
  const index = getDepositFollowUpCheckpointIndex(checkpoint);
  return index >= 0 && index < DEPOSIT_FOLLOW_UP_CHECKPOINTS.length - 1
    ? DEPOSIT_FOLLOW_UP_CHECKPOINTS[index + 1].value
    : null;
};

const DepositFollowUpTimeline = ({
  orderId,
  checkpoint,
  onUpdate,
  compact = false,
  className = "",
}) => {
  const [updating, setUpdating] = useState(false);
  const current = normalizeDepositFollowUpCheckpoint(checkpoint);
  const currentIndex = getDepositFollowUpCheckpointIndex(current);
  const nextCheckpoint = getNextDepositFollowUpCheckpoint(current);

  const applyCheckpoint = async (newCheckpoint) => {
    if (newCheckpoint === current || updating) return;

    const target = DEPOSIT_FOLLOW_UP_CHECKPOINTS.find((c) => c.value === newCheckpoint);
    const confirmed = window.confirm(
      `تحديث مرحلة المتابعة إلى:\n${target?.labelAr || newCheckpoint}؟`
    );
    if (!confirmed) return;

    setUpdating(true);
    try {
      await onUpdate(orderId, newCheckpoint);
    } finally {
      setUpdating(false);
    }
  };

  const handleAdvance = () => {
    if (nextCheckpoint) applyCheckpoint(nextCheckpoint);
  };

  return (
    <div className={`${className}`} dir="rtl">
      <div className={`space-y-1 ${compact ? "text-xs" : "text-sm"}`}>
        {DEPOSIT_FOLLOW_UP_CHECKPOINTS.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isPending = index > currentIndex;

          return (
            <button
              key={step.value}
              type="button"
              disabled={updating || isCurrent}
              onClick={() => applyCheckpoint(step.value)}
              className={`w-full flex items-start gap-2 rounded-lg px-2 py-1.5 text-right transition ${
                isCurrent
                  ? "bg-orange-100 border border-orange-300 cursor-default"
                  : isCompleted
                  ? "bg-green-50/80 hover:bg-green-100 border border-transparent"
                  : "bg-gray-50 hover:bg-orange-50 border border-transparent"
              } ${updating ? "opacity-60" : ""}`}
              title={isCurrent ? "المرحلة الحالية" : "اضغط لتعيين هذه المرحلة"}
            >
              <span className="mt-0.5 flex-shrink-0">
                {isCompleted ? (
                  <CheckCircle2 size={compact ? 14 : 16} className="text-green-600" />
                ) : (
                  <Circle
                    size={compact ? 14 : 16}
                    className={isCurrent ? "text-orange-600 fill-orange-200" : "text-gray-400"}
                  />
                )}
              </span>
              <span
                className={`leading-snug ${
                  isCurrent
                    ? "font-bold text-orange-800"
                    : isCompleted
                    ? "text-green-800"
                    : isPending
                    ? "text-gray-500"
                    : ""
                }`}
              >
                {step.labelAr}
              </span>
            </button>
          );
        })}
      </div>

      {nextCheckpoint && (
        <button
          type="button"
          disabled={updating}
          onClick={handleAdvance}
          className={`mt-2 w-full flex items-center justify-center gap-1 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700 transition disabled:opacity-50 ${
            compact ? "py-1.5 text-xs" : "py-2 text-sm"
          }`}
        >
          {updating ? (
            <Loader size={14} className="animate-spin" />
          ) : (
            <>
              <ChevronRight size={14} />
              المرحلة التالية
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default DepositFollowUpTimeline;
